import { useState, useRef, useEffect } from 'react';

// This is your chatbot's address (from Step 5)
const CHATBOT_URL = 'https://fivetrader-chatbot.oschaudhary19.workers.dev/chat';

export default function ChatWidget() {
  // State = things that change
  const [isOpen, setIsOpen] = useState(false);        // Is chat box open?
  const [messages, setMessages] = useState([           // List of messages
    { from: 'bot', text: 'Hi! Ask me about FiveTrader.' }
  ]);
  const [inputText, setInputText] = useState('');      // What user is typing
  const [isTyping, setIsTyping] = useState(false);    // Is bot thinking?
  
  const messagesEnd = useRef(null);  // For auto-scroll
  
  // Generate or remember session ID
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('chat_session');
    if (!id) {
      id = Math.random().toString(36).substring(2) + Date.now();
      localStorage.setItem('chat_session', id);
    }
    return id;
  });

  // Auto-scroll to bottom when new messages come
  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // When user sends a message
  const sendMessage = async () => {
    if (!inputText.trim() || isTyping) return;
    
    const userMessage = inputText.trim();
    setInputText('');  // Clear input box
    
    // Add user message to screen
    setMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setIsTyping(true);
    
    try {
      // Send to chatbot
      const response = await fetch(CHATBOT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });
      
      const data = await response.json();
      
      // Add bot's answer to screen
      setMessages(prev => [...prev, { from: 'bot', text: data.answer }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: 'Sorry, I am having trouble connecting. Please try again later.' 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // Press Enter to send
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <>
      {/* Floating Chat Button (bottom right corner) - always shows 💬 */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          💬
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '380px',
          height: '500px',
          maxHeight: 'calc(100vh - 40px)',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 10000,
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          {/* Header with close button */}
          <div style={{
            backgroundColor: '#2563eb',
            color: 'white',
            padding: '16px',
            fontWeight: 'bold',
            fontSize: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>FiveTrader Assistant</span>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ✕
            </button>
          </div>

          {/* Messages Area */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            backgroundColor: '#f9fafb'
          }}>
            {messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  alignSelf: msg.from === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  backgroundColor: msg.from === 'user' ? '#2563eb' : 'white',
                  color: msg.from === 'user' ? 'white' : '#1f2937',
                  padding: '10px 14px',
                  borderRadius: msg.from === 'user' 
                    ? '16px 16px 4px 16px' 
                    : '16px 16px 16px 4px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  boxShadow: msg.from === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                {msg.text}
              </div>
            ))}
            
            {isTyping && (
              <div style={{
                alignSelf: 'flex-start',
                backgroundColor: 'white',
                padding: '10px 14px',
                borderRadius: '16px 16px 16px 4px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Typing...
              </div>
            )}
            
            <div ref={messagesEnd} />
          </div>

          {/* Input Area */}
          <div style={{
            padding: '12px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '8px',
            backgroundColor: 'white'
          }}>
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about trading..."
              className="!text-gray-900"
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '20px',
                outline: 'none',
                fontSize: '14px',
                backgroundColor: '#ffffff',
                caretColor: '#1f2937',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={isTyping}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                opacity: isTyping ? 0.6 : 1
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}